import React from "react";
import {
    SelectItem,
} from "@heroui/react";
import AppSelect from "../../../components/common/appSelect";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "@heroui/react";

interface FinancialInformationProps {
    t: any;
    selectedPurse: string;
    setSelectedPurse: (value: string) => void;
    setPurse: (value: string) => void;
    purses: string[];
    errors: any;
    setErrors: (value: any) => void;
    loadPursesIfNeeded: () => Promise<void>;
    formatSportName: (sport: string) => string;
    modalCurrency: string;
    setModalCurrency: (value: string) => void;
    setCurrency: (value: string) => void;
    currencies: string[];
    loadCurrenciesIfNeeded: () => Promise<void>;
}

const FinancialInformation: React.FC<FinancialInformationProps> = ({
    t,
    selectedPurse,
    setSelectedPurse,
    setPurse,
    purses,
    errors,
    setErrors,
    loadPursesIfNeeded,
    formatSportName,
    modalCurrency,
    setModalCurrency,
    setCurrency,
    currencies,
    loadCurrenciesIfNeeded,
}) => {
    return (
        <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-800 border-b pb-1">
                {t("financial_information")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600 block">
                        <div className="flex items-center gap-1">
                            {t("purse")}
                            <Tooltip
                                className="w-[350px]"
                                placement="right"
                                content={
                                    <div className="text-sm">
                                        <span className="font-semibold block mb-1">
                                            {t("purse_tooltip_title")}
                                        </span>
                                        <span>
                                            {t("purse_tooltip_description")}
                                        </span>
                                    </div>
                                }
                            >
                                <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                            </Tooltip>
                        </div>
                    </label>
                    <AppSelect
                        placeholder={t("select_purse_placeholder")}
                        selectedKeys={selectedPurse ? [selectedPurse] : []}
                        onSelectionChange={(key) => {
                            const selected = Array.from(key)[0] as string;
                            setSelectedPurse(selected);
                            setPurse(selected);
                            setErrors((prev: any) => ({
                                ...prev,
                                purse: selected ? "" : t("purse_required"),
                            }));
                        }}
                        onOpenChange={(isOpen) =>
                            isOpen && loadPursesIfNeeded?.()
                        }
                        className="w-full"
                        size="sm"
                    >
                        {purses.length > 0 ? (
                            purses.map((purse) => (
                                <SelectItem key={purse}>
                                    {formatSportName(purse)}
                                </SelectItem>
                            ))
                        ) : (
                            <SelectItem key="no-purse" isDisabled>
                                {t("no_purse_options")}
                            </SelectItem>
                        )}
                    </AppSelect>
                    {errors.purse && (
                        <div className="text-red-500 text-xs">
                            {errors.purse}
                        </div>
                    )}
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-600 block">
                        {t("currency")}
                    </label>
                    <AppSelect
                        selectedKeys={modalCurrency ? [modalCurrency] : []}
                        onSelectionChange={(key) => {
                            const selected = Array.from(key)[0] as string;
                            setModalCurrency(selected);
                            setCurrency(selected);
                            setErrors((prev: any) => ({
                                ...prev,
                                currency: selected ? "" : t("currency_required"),
                            }));
                        }}
                        onOpenChange={(isOpen) =>
                            isOpen && loadCurrenciesIfNeeded?.()
                        }
                        size="sm"
                        radius="md"
                        className="w-full text-xs"
                        placeholder={t("select_currency_placeholder")}
                    >
                        {currencies.map((currency) => (
                            <SelectItem key={currency}>{currency}</SelectItem>
                        ))}
                    </AppSelect>
                    {errors.currency && (
                        <div className="text-red-500 text-xs">
                            {errors.currency}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FinancialInformation;
